import { InitialForm, useAdministrationModel } from './useAdministration.model';
import type { LandingPageVisualVersion } from './administrationConfig';
import { Actions, Async, FSpread } from '@client/new/helpers/types';
import _ from 'lodash';
import auth from '@client/components/auth';
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormWatch,
} from 'react-hook-form';
import { Influencer } from '@playbook-technologies/common-enums';
import { landingPagePreviewUrl } from '@client/new/common/landingPagePreview';

export type { LandingPageVisualVersion };

type AdministrationViewModelParams = {
  influencerId: number;
};

export type PayloadMap = {
  resetStripe: null;
  updateInfluencer: null;
  grantOwnSubscriptionAccess: null;
  setTags: string[];
  setCustomizations: object;
  setLandingPageVisualVersion: LandingPageVisualVersion;
};

type AdministrationViewActions = Actions<PayloadMap>;
export type AdministrationViewDispatcher = FSpread<PayloadMap>;

export type AdministrationViewModel = {
  dispatch: AdministrationViewDispatcher;
  register: UseFormRegister<InitialForm>;
  watch: UseFormWatch<InitialForm>;
  control: Control<InitialForm, any, InitialForm>;
  submittingReset: boolean;
  submittingUpdate: boolean;
  submittingGrantOwnSubscription: boolean;
  hasAccessToOwnExclusiveSubscription: boolean;
  isPaymentEnabled: boolean;
  paymentMethod: number | null;
  tags: string[];
  customizations: object;
  landingPageVisualVersion: LandingPageVisualVersion;
  landingPagePreviewUrl: string | null;
  stripeUserId: string | null;
  isDeepLinkDisabled: boolean;
  disableSuperAdminSections: boolean;
  showStripe: boolean;
  hidePublishedAt: boolean;
};

export const useAdministrationViewModel = (
  p: AdministrationViewModelParams,
): Async<AdministrationViewModel> => {
  const asyncModel = useAdministrationModel(p);

  if (asyncModel.type !== 'fetched') return asyncModel;

  const { watch } = asyncModel.value;
  const isPaymentEnabled = watch('isPaymentEnabled');
  const paymentMethod = watch('paymentMethod');
  const tags = watch('tags');
  const customizations = watch('customizations');
  const mode = watch('mode');
  const customURL = watch('customURL');

  const model = asyncModel.value;

  const dispatch: AdministrationViewDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as AdministrationViewActions;
    switch (action.type) {
      case 'resetStripe':
        return model.dispatch(action.type);
      case 'updateInfluencer':
        return model.dispatch(action.type);
      case 'grantOwnSubscriptionAccess':
        return model.dispatch(action.type);
      case 'setTags':
        return model.dispatch(action.type, action.payload);
      case 'setCustomizations':
        return model.dispatch(action.type, action.payload);
      case 'setLandingPageVisualVersion':
        return model.dispatch('setCustomizations', {
          ...customizations,
          landingPageVisualVersion: action.payload,
        });
    }
  };

  const showStripe = auth.hasStripePermission();
  const hidePublishedAt = mode === Influencer.Mode.Onboarding.value;

  return {
    type: 'fetched',
    value: {
      ...asyncModel.value,
      dispatch,
      isPaymentEnabled,
      paymentMethod,
      tags,
      customizations,
      landingPagePreviewUrl: customURL
        ? landingPagePreviewUrl(customURL)
        : null,
      isDeepLinkDisabled: auth.isModerator(),
      disableSuperAdminSections: !auth.isAdministrator(),
      showStripe,
      hidePublishedAt,
    },
  };
};
